import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  Check,
  GitBranchPlus,
  Play,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import {
  deliveryDelayDays,
  formatDeliveryDelayLabel,
  getDeliveryStatusOptions,
  getDeliveryStatusStyle,
  isAwaitingProductionDeliveryStatus,
  isCancelledDeliveryStatus,
  isDeliveredDeliveryStatus,
  isIncidentDeliveryStatus,
  isInTransitDeliveryStatus,
  isRescheduledDeliveryStatus,
  normalizeDeliveryStatus,
} from '../../utils/deliveryStatus';

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>{bottom}</div>
    ) : null}
  </div>
);

const parseYmd = (value) => {
  const raw = String(value || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const formatDateEs = (iso) => {
  const d = parseYmd(iso) || (iso ? new Date(iso) : null);
  if (!d || Number.isNaN(d.getTime())) return iso ? String(iso) : '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateShort = (iso) => {
  const d = parseYmd(iso);
  if (!d) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};

const todayLocal = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** Línea de avance: inicio/programada → estimada, con marcador de fecha prometida. */
const DeliveryProgressTimeline = ({ row, text, muted, trackBg, isDarkMode }) => {
  const startIso = row.scheduled_date || null;
  const estimatedIso = row.estimated_delivery_date || null;
  const promisedIso = row.promised_delivery_date || row.expected_delivery_date || null;

  const start = parseYmd(startIso);
  const estimated = parseYmd(estimatedIso);
  const promised = parseYmd(promisedIso);
  const today = todayLocal();

  if (!start && !estimated && !promised) {
    return <span style={{ fontSize: '11px', color: muted }}>—</span>;
  }

  const status = normalizeDeliveryStatus(row.delivery_status);
  const isDelivered = isDeliveredDeliveryStatus(status);
  const axisDates = [start, estimated, promised];
  if (!estimated && !isDelivered) axisDates.push(today);
  const validAxis = axisDates.filter(Boolean);
  const axisStart = new Date(Math.min(...validAxis.map((d) => d.getTime())));
  const axisEnd = new Date(Math.max(...validAxis.map((d) => d.getTime())));
  const spanMs = Math.max(axisEnd.getTime() - axisStart.getTime(), 24 * 60 * 60 * 1000);

  const pct = (dt) => {
    if (!dt) return 50;
    return Math.min(100, Math.max(0, ((dt.getTime() - axisStart.getTime()) / spanMs) * 100));
  };

  const progressPct = isDelivered
    ? 100
    : Math.min(100, Math.max(0, ((today.getTime() - axisStart.getTime()) / spanMs) * 100));

  const promisedPct = promised ? pct(promised) : null;
  const promisedLate = Boolean(promised) && !isDelivered && today.getTime() > promised.getTime();
  const promisedColor = promisedLate ? '#dc2626' : isDarkMode ? '#fbbf24' : '#d97706';

  return (
    <div
      style={{ minWidth: '168px', maxWidth: '220px', padding: '2px 0' }}
      title={[
        startIso ? `Inicio/programada: ${formatDateEs(startIso)}` : null,
        estimatedIso ? `Estimada: ${formatDateEs(estimatedIso)}` : null,
        promisedIso ? `Prometida: ${formatDateEs(promisedIso)}` : null,
      ]
        .filter(Boolean)
        .join('\n')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
        <span style={{ fontSize: '10px', fontWeight: 600, color: text, whiteSpace: 'nowrap' }}>
          {formatDateShort(startIso)}
        </span>
        <span style={{ fontSize: '10px', fontWeight: 600, color: muted, whiteSpace: 'nowrap' }}>
          {estimatedIso ? formatDateShort(estimatedIso) : '—'}
        </span>
      </div>
      <div style={{ position: 'relative', height: '10px' }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '4px',
            height: '3px',
            borderRadius: '999px',
            background: trackBg,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: '4px',
            height: '3px',
            width: `${progressPct}%`,
            borderRadius: '999px',
            background: isDelivered ? '#16a34a' : isDarkMode ? '#60a5fa' : '#2563eb',
          }}
        />
        {promisedPct != null ? (
          <div
            style={{
              position: 'absolute',
              left: `calc(${promisedPct}% - 5px)`,
              top: '0px',
              width: '10px',
              height: '10px',
              borderRadius: '2px',
              background: promisedColor,
              border: `1.5px solid ${isDarkMode ? '#18181b' : '#ffffff'}`,
              transform: 'rotate(45deg)',
              boxSizing: 'border-box',
            }}
            title={`Prometida: ${formatDateEs(promisedIso)}`}
          />
        ) : null}
      </div>
      {promisedIso ? (
        <div
          style={{
            fontSize: '9px',
            color: promisedColor,
            marginTop: '3px',
            textAlign:
              promisedPct != null && promisedPct > 70
                ? 'right'
                : promisedPct != null && promisedPct < 30
                  ? 'left'
                  : 'center',
          }}
        >
          Prometida {formatDateShort(promisedIso)}
        </div>
      ) : (
        <div style={{ fontSize: '9px', color: muted, marginTop: '3px' }}>Sin fecha prometida</div>
      )}
    </div>
  );
};

const TRIP_STATUS_STYLES = {
  Planificado: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  'En Ruta': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  'En Curso': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  Completado: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Finalizado: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Incidencia: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  Cancelado: { bg: '#f4f4f5', text: '#52525b', border: '#d4d4d8' },
  default: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
};

const formatMoneyCl = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const VARIANT_CONFIG = {
  deliveries: {
    title: 'Entregas',
    entityLabel: 'entregas',
    tableCode: 'A011_deliveries',
    addLabel: 'Nueva entrega',
    emptyLabel: 'No hay entregas para mostrar.',
    loadingLabel: 'Cargando entregas…',
    searchPlaceholder: 'Código DLV, venta, cliente, estado…',
    statusField: 'delivery_status',
    statusFilterLabel: 'Estado',
    statusFilterOptions: 'catalog',
    typeField: 'dispatch_tipo',
    typeFilterLabel: 'Tipo',
    typeFilterOptions: [
      'Despacho Completo',
      'Despacho Parcial',
      'Reposición',
      'Retiro en Bodega',
    ],
    defaultSort: 'delivery_code',
    defaultSortDir: 'desc',
    minWidth: '1420px',
    columns: [
      { key: 'Código', align: 'left', sortKey: 'delivery_code' },
      { key: 'Cliente', align: 'left', sortKey: 'organization_name' },
      { key: 'Tipo', align: 'left', sortKey: 'dispatch_tipo' },
      { key: 'Avance', align: 'left', sortKey: 'promised_delivery_date' },
      { key: 'Peso', align: 'right', sortKey: 'weight_kg' },
      { key: 'Estado', align: 'center', sortKey: 'delivery_status' },
    ],
  },
  legs: {
    title: 'Tramos',
    entityLabel: 'tramos',
    tableCode: 'A012_delivery_legs',
    addLabel: null,
    emptyLabel: 'No hay tramos para mostrar.',
    loadingLabel: 'Cargando tramos…',
    searchPlaceholder: 'Código tramo, entrega, viaje, transportista…',
    statusField: 'leg_status',
    statusFilterLabel: 'Estado',
    typeField: 'trip_type',
    typeFilterLabel: 'Tipo viaje',
    defaultSort: 'leg_code',
    defaultSortDir: 'desc',
    minWidth: '1180px',
    columns: [
      { key: 'Tramo', align: 'left', sortKey: 'leg_code' },
      { key: 'Entrega', align: 'left', sortKey: 'delivery_code' },
      { key: 'Viaje', align: 'left', sortKey: 'trip_code' },
      { key: 'Transportista', align: 'left', sortKey: 'carrier_name' },
      { key: 'ETA', align: 'left', sortKey: 'eta_close' },
      { key: 'Estado', align: 'center', sortKey: 'leg_status' },
    ],
  },
  trips: {
    title: 'Viajes',
    entityLabel: 'viajes',
    tableCode: 'A013_delivery_trips',
    addLabel: 'Nuevo viaje',
    emptyLabel: 'No hay viajes para mostrar.',
    loadingLabel: 'Cargando viajes…',
    searchPlaceholder: 'Código, tipo, transportista, origen, destino…',
    statusField: 'trip_status',
    statusFilterLabel: 'Estado',
    typeField: 'trip_type',
    typeFilterLabel: 'Tipo viaje',
    defaultSort: 'trip_date',
    defaultSortDir: 'desc',
    minWidth: '1100px',
    columns: [
      { key: 'Viaje', align: 'left', sortKey: 'trip_code' },
      { key: 'Tipo', align: 'left', sortKey: 'trip_type' },
      { key: 'Costo', align: 'right', sortKey: 'cost' },
      { key: 'Tramos', align: 'left', sortKey: 'legs_count' },
      { key: 'Ruta', align: 'left', sortKey: 'destination_location' },
      { key: 'Estado', align: 'center', sortKey: 'trip_status' },
    ],
  },  calendar: {
    title: 'Detalle de entregas',
    entityLabel: 'entregas',
    tableCode: 'calendario',
    addLabel: null,
    emptyLabel: 'No hay entregas programadas.',
    loadingLabel: 'Cargando calendario…',
    searchPlaceholder: 'Venta, cotización, organización…',
    statusField: 'delivery_status',
    statusFilterLabel: 'Estado',
    statusFilterOptions: 'catalog',
    typeField: 'dispatch_tipo',
    typeFilterLabel: 'Tipo',
    typeFilterOptions: [
      'Despacho Completo',
      'Despacho Parcial',
      'Reposición',
      'Retiro en Bodega',
    ],
    defaultSort: 'scheduled_date',
    defaultSortDir: 'asc',
    minWidth: '980px',
    columns: [
      { key: 'Fecha', align: 'left', sortKey: 'scheduled_date' },
      { key: 'Venta', align: 'left', sortKey: 'sale_code' },
      { key: 'Cliente', align: 'left', sortKey: 'organization_name' },
      { key: 'Tipo', align: 'left', sortKey: 'dispatch_tipo' },
      { key: 'Recibe', align: 'left', sortKey: 'receiver_name' },
      { key: 'Estado', align: 'center', sortKey: 'delivery_status' },
    ],
  },
};

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'sale_code':
      return String(row.sale_code || row.quotation_code || '').toLowerCase();
    case 'delivery_code':
      return String(row.delivery_code || '').toLowerCase();
    case 'delivery_kind':
      return String(row.dispatch_tipo || row.delivery_kind || row.delivery_type || '').toLowerCase();
    case 'dispatch_tipo':
      return String(row.dispatch_tipo || row.delivery_kind || row.delivery_type || '').toLowerCase();
    case 'weight_kg':
      return Number(row.weight_kg) || 0;
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'delivery_type':
      return String(row.dispatch_tipo || row.delivery_type || '').toLowerCase();
    case 'scheduled_date':
      return String(row.scheduled_date || '');
    case 'promised_delivery_date':
      return String(row.promised_delivery_date || row.expected_delivery_date || row.scheduled_date || '');
    case 'estimated_delivery_date':
      return String(row.estimated_delivery_date || row.delivered_date || '');
    case 'carrier_name':
      return String(row.carrier_name || row.provider_name || '').toLowerCase();
    case 'delivery_status':
      return String(row.delivery_status || '').toLowerCase();
    case 'leg_code':
      return String(row.leg_code || '').toLowerCase();
    case 'leg_status':
      return String(row.leg_status || '').toLowerCase();
    case 'eta_close':
      return String(row.eta_close || '');
    case 'trip_type':
      return String(row.trip_type || '').toLowerCase();
    case 'legs_count':
      return Number(row.legs_count || row.deliveries_count) || 0;
    case 'provider_code':
      return String(row.provider_code || row.provider_name || row.carrier_name || '').toLowerCase();
    case 'trip_code':
      return String(row.trip_code || '').toLowerCase();
    case 'cost':
      return Number(row.cost) || 0;
    case 'deliveries_count':
      return Number(row.deliveries_count) || 0;
    case 'destination_location':
      return String(row.destination_location || row.destination_summary || '').toLowerCase();
    case 'trip_status':
      return String(row.trip_status || '').toLowerCase();
    case 'trip_date':
      return String(row.trip_date || '');
    case 'receiver_name':
      return String(row.receiver_name || '').toLowerCase();
    case 'dispatch_guide':
      return String(row.dispatch_guide || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const statusPalette = (variant, status) => {
  const key = String(status || '').trim();
  if (variant === 'trips') {
    return TRIP_STATUS_STYLES[key] || TRIP_STATUS_STYLES.default;
  }
  // deliveries + legs: colores desde Ajustes → Estados Despacho
  return getDeliveryStatusStyle(key);
};

/**
 * Tabla de despachos (entregas / viajes / calendario): mismo look que Table3 / SalesTable3.
 */
const LogisticsTable3 = ({
  variant = 'deliveries',
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle,
  addButtonLabel,
  hideChrome = false,
  onAdd,
  onRowClick,
  onStartDelivery,
  onContinueLeg,
  onFinishDelivery,
  onReviewReschedule,
  onBulkDelete,
}) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.deliveries;
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortColumn, setSortColumn] = useState(config.defaultSort);
  const [sortDir, setSortDir] = useState(config.defaultSortDir);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete);
  const showActions =
    variant === 'deliveries' &&
    Boolean(onStartDelivery || onContinueLeg || onFinishDelivery || onReviewReschedule);
  const columns = useMemo(() => {
    if (!showActions) return config.columns;
    return [...config.columns, { key: 'Acciones', align: 'center', sortKey: null }];
  }, [config.columns, showActions]);
  const colCount = (showBulkChrome ? 1 : 0) + columns.length;
  const title = tableTitle || config.title;
  const addLabel = addButtonLabel || config.addLabel;

  const statusOptions = useMemo(() => {
    if (config.statusFilterOptions === 'catalog') {
      return getDeliveryStatusOptions();
    }
    if (Array.isArray(config.statusFilterOptions) && config.statusFilterOptions.length > 0) {
      return config.statusFilterOptions;
    }
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r[config.statusField] || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows, config.statusField, config.statusFilterOptions]);

  const typeOptions = useMemo(() => {
    if (Array.isArray(config.typeFilterOptions) && config.typeFilterOptions.length > 0) {
      return config.typeFilterOptions;
    }
    const map = new Map();
    rows.forEach((r) => {
      const t = String(r[config.typeField] || '').trim();
      if (t) map.set(t, t);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows, config.typeField, config.typeFilterOptions]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = Object.values(row || {})
          .filter((x) => x !== undefined && x !== null && typeof x !== 'object')
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) {
      list = list.filter((row) => {
        const raw = String(row[config.statusField] || '').trim();
        if (variant === 'deliveries' || variant === 'calendar') {
          return normalizeDeliveryStatus(raw) === filterStatus;
        }
        return raw === filterStatus;
      });
    }
    if (filterType) {
      list = list.filter((row) => String(row[config.typeField] || '').trim() === filterType);
    }
    return list;
  }, [rows, searchQuery, filterStatus, filterType, config.statusField, config.typeField, variant]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const allVisibleSelected =
    sortedRows.length > 0 && sortedRows.every((row) => selectedIds.includes(Number(row.id)));
  const someVisibleSelected = sortedRows.some((row) => selectedIds.includes(Number(row.id)));

  useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  useEffect(() => {
    setSelectedIds([]);
    setFilterStatus('');
    setFilterType('');
    setSearchQuery('');
    setSortColumn(config.defaultSort);
    setSortDir(config.defaultSortDir);
  }, [variant, config.defaultSort, config.defaultSortDir]);

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visible = new Set(sortedRows.map((r) => Number(r.id)));
      setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
    } else {
      const visibleIds = sortedRows.map((r) => Number(r.id));
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleSelectOne = (id) => {
    const nid = Number(id);
    setSelectedIds((prev) => (prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]));
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const singular = config.entityLabel.replace(/es$/, '').replace(/s$/, '');
    const ok = window.confirm(
      `¿Eliminar ${n} ${n === 1 ? singular : config.entityLabel} seleccionado${n === 1 ? '' : 's'}?`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      const selectedRows = rows.filter((r) => selectedIds.includes(Number(r.id)));
      await onBulkDelete(selectedRows);
      setSelectedIds([]);
    } catch (e) {
      setBulkError(e?.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterType('');
  };

  const hasActiveFilters = Boolean(searchQuery.trim()) || Boolean(filterStatus) || Boolean(filterType);
  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} ${config.entityLabel} · ${config.tableCode}`
        : `${rows.length} ${config.entityLabel} · ${config.tableCode}`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const renderCells = (row) => {
    const rawStatus = row[config.statusField];
    const status =
      variant === 'deliveries' || variant === 'calendar'
        ? normalizeDeliveryStatus(rawStatus)
        : rawStatus;
    const palette = statusPalette(variant, status);
    const linkColor = ui.link || '#2563eb';
    const delayLabel =
      variant === 'deliveries' || variant === 'calendar'
        ? formatDeliveryDelayLabel(deliveryDelayDays(row))
        : null;

    if (variant === 'trips') {
      const initial = String(row.trip_code || '?').trim().charAt(0).toUpperCase();
      return (
        <>
          <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
            <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
              <div
                style={{
                  width: '3px',
                  flexShrink: 0,
                  background: palette.border,
                  borderRadius: '0 2px 2px 0',
                }}
              />
              <div style={{ display: 'flex', gap: '6px', padding: `2px ${padH + 2}px`, alignItems: 'center' }}>
                <div
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '7px',
                    background: isDarkMode ? '#27272a' : '#f0ebe3',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 800,
                    fontSize: '12px',
                    color: text,
                    flexShrink: 0,
                  }}
                >
                  {initial}
                </div>
                <div>
                  {onRowClick ? (
                    <button
                      type="button"
                      onClick={() => onRowClick(row)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '12px',
                        color: linkColor,
                        lineHeight: 1.2,
                        textAlign: 'left',
                      }}
                    >
                      {row.trip_code || '—'}
                    </button>
                  ) : (
                    <div style={{ fontWeight: 700, fontSize: '12px', color: text }}>{row.trip_code || '—'}</div>
                  )}
                  <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>{formatDateEs(row.trip_date)}</div>
                </div>
              </div>
            </div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>
              {row.trip_type || row.provider_name || '—'}
            </div>
            <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>
              {row.carrier_name || row.provider_name || '—'}
            </div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', verticalAlign: 'middle' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>{formatMoneyCl(row.cost)}</div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <Stacked
              top={`${Number(row.legs_count || row.deliveries_count) || 0} tramo${Number(row.legs_count || row.deliveries_count) === 1 ? '' : 's'}`}
              bottom={row.sale_codes || null}
              topColor={text}
              mutedColor={muted}
            />
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '220px' }}>
            <Stacked
              top={row.destination_location || row.destination_summary || '—'}
              bottom={row.origin_location ? `${row.origin_location} →` : null}
              topColor={text}
              mutedColor={muted}
            />
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '10px',
                fontWeight: 700,
                background: palette.bg,
                color: palette.text,
                border: `1px solid ${palette.border}`,
              }}
            >
              {status || '—'}
            </span>
          </td>
        </>
      );
    }

    if (variant === 'calendar') {
      return (
        <>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>{formatDateEs(row.scheduled_date)}</div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <Stacked
              top={row.sale_code || '—'}
              bottom={row.quotation_code || null}
              topColor={text}
              mutedColor={muted}
            />
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '220px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>{row.organization_name || '—'}</div>
            <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>{row.delivery_address || '—'}</div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <div style={{ fontSize: '12px', color: text }}>
              {row.dispatch_tipo || row.delivery_type || '—'}
            </div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <div style={{ fontSize: '12px', color: text }}>{row.receiver_name || '—'}</div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                lineHeight: 1.2,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  fontSize: '10px',
                  fontWeight: 700,
                  background: palette.bg,
                  color: palette.text,
                  border: `1px solid ${palette.border}`,
                  maxWidth: '160px',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                }}
              >
                {status || '—'}
              </span>
              {delayLabel ? (
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#dc2626' }}>{delayLabel}</span>
              ) : null}
            </div>
          </td>
        </>
      );
    }

    if (variant === 'legs') {
      return (
        <>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <div style={{ fontWeight: 700, fontSize: '12px', color: linkColor }}>{row.leg_code || '—'}</div>
            <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>
              {row.sequence ? `Secuencia ${row.sequence}` : '—'}
            </div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <Stacked
              top={row.delivery_code || '—'}
              bottom={row.sale_code || null}
              topColor={text}
              mutedColor={muted}
            />
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <Stacked
              top={row.trip_code || 'Sin viaje'}
              bottom={row.trip_type || null}
              topColor={text}
              mutedColor={muted}
            />
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <Stacked
              top={row.carrier_name || '—'}
              bottom={row.tracking_code || null}
              topColor={text}
              mutedColor={muted}
            />
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
            <div style={{ fontSize: '12px', color: text }}>{formatDateEs(row.eta_close)}</div>
          </td>
          <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '10px',
                fontWeight: 700,
                background: palette.bg,
                color: palette.text,
                border: `1px solid ${palette.border}`,
              }}
            >
              {status || '—'}
            </span>
          </td>
        </>
      );
    }

    // deliveries (default)
    const codeLabel = row.delivery_code || row.sale_code || '—';
    const initial = String(codeLabel).replace(/^DLV/i, '').trim().charAt(0).toUpperCase() || '?';
    return (
      <>
        <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
            <div
              style={{
                width: '3px',
                flexShrink: 0,
                background: palette.border,
                borderRadius: '0 2px 2px 0',
              }}
            />
            <div style={{ display: 'flex', gap: '6px', padding: `2px ${padH + 2}px`, alignItems: 'center' }}>
              <div
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '7px',
                  background: isDarkMode ? '#27272a' : '#f0ebe3',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 800,
                  fontSize: '12px',
                  color: text,
                  flexShrink: 0,
                }}
              >
                {initial}
              </div>
              <div>
                {onRowClick ? (
                  <button
                    type="button"
                    onClick={() => onRowClick(row)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '12px',
                      color: linkColor,
                      lineHeight: 1.2,
                      textAlign: 'left',
                    }}
                  >
                    {row.delivery_code || 'Sin código'}
                  </button>
                ) : (
                  <div style={{ fontWeight: 700, fontSize: '12px', color: text }}>
                    {row.delivery_code || 'Sin código'}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>
                  {[row.sale_code, row.quotation_code].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '220px' }}>
          <Stacked
            top={row.organization_name || '—'}
            bottom={row.delivery_address || null}
            topColor={text}
            mutedColor={muted}
          />
        </td>
        <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>
            {row.dispatch_tipo || row.delivery_kind || 'Despacho Completo'}
          </div>
        </td>
        <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
          <DeliveryProgressTimeline
            row={row}
            text={text}
            muted={muted}
            trackBg={isDarkMode ? '#3f3f46' : '#e4e4e7'}
            isDarkMode={isDarkMode}
          />
        </td>
        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', verticalAlign: 'middle' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: text }}>
            {Number(row.weight_kg) > 0
              ? `${Number(row.weight_kg).toLocaleString('es-CL', { maximumFractionDigits: 2 })} kg`
              : '—'}
          </div>
        </td>
        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
              lineHeight: 1.2,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '10px',
                fontWeight: 700,
                background: palette.bg,
                color: palette.text,
                border: `1px solid ${palette.border}`,
                maxWidth: '160px',
                whiteSpace: 'normal',
                textAlign: 'center',
              }}
            >
              {status || '—'}
            </span>
            {delayLabel ? (
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#dc2626' }}>{delayLabel}</span>
            ) : null}
          </div>
        </td>
        {showActions ? (
          <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
            {(() => {
              const delivered = isDeliveredDeliveryStatus(status);
              const inTransit = isInTransitDeliveryStatus(status);
              const incident = isIncidentDeliveryStatus(status);
              const awaiting = isAwaitingProductionDeliveryStatus(status);
              const rescheduled = isRescheduledDeliveryStatus(status);
              const cancelled = isCancelledDeliveryStatus(status);
              const blockedOps = delivered || inTransit || incident || awaiting || rescheduled || cancelled;
              const canStart = Boolean(onStartDelivery) && !blockedOps;
              const canContinue = Boolean(onContinueLeg) && !delivered && !incident && !cancelled && !awaiting;
              const canFinish = Boolean(onFinishDelivery) && !delivered && !incident && !cancelled && !awaiting;
              const canReviewReschedule = Boolean(onReviewReschedule) && rescheduled;
              const actionBtn = ({ label, title, icon, onClick, enabled, tone }) => {
                const activeBg =
                  tone === 'start'
                    ? '#2563eb'
                    : tone === 'finish'
                      ? '#16a34a'
                      : tone === 'reschedule'
                        ? '#9333ea'
                        : inputBg;
                const activeColor = tone === 'continue' ? text : '#fff';
                const activeBorder =
                  tone === 'continue' ? inputBorder : 'transparent';
                return (
                  <button
                    type="button"
                    title={title}
                    disabled={!enabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (enabled) onClick(row);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      height: '26px',
                      padding: '0 8px',
                      borderRadius: '6px',
                      border: `1px solid ${enabled ? activeBorder : inputBorder}`,
                      background: enabled ? activeBg : isDarkMode ? '#27272a' : '#f4f4f5',
                      color: enabled ? activeColor : muted,
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: enabled ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap',
                      opacity: enabled ? 1 : 0.55,
                    }}
                  >
                    {icon}
                    {label}
                  </button>
                );
              };
              return (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    flexWrap: 'wrap',
                  }}
                >
                  {canReviewReschedule ? (
                    actionBtn({
                      label: 'Reprogramar',
                      title: 'Revisar reprogramación de producción (aceptar o cancelar)',
                      icon: <CalendarClock size={12} />,
                      onClick: onReviewReschedule,
                      enabled: true,
                      tone: 'reschedule',
                    })
                  ) : (
                    <>
                      {actionBtn({
                        label: 'Iniciar',
                        title: canStart
                          ? 'Iniciar entrega (asignar viaje al tramo inicial)'
                          : awaiting
                            ? 'Esperando aceptación de producción'
                            : rescheduled
                              ? 'Hay una reprogramación pendiente de revisar'
                              : delivered
                                ? 'La entrega ya está finalizada'
                                : inTransit
                                  ? 'La entrega ya está en ruta'
                                  : 'No se puede iniciar esta entrega',
                        icon: <Play size={12} />,
                        onClick: onStartDelivery,
                        enabled: canStart,
                        tone: 'start',
                      })}
                      {actionBtn({
                        label: 'Continuar',
                        title: canContinue
                          ? 'Continuar tramo (mismo código de entrega, nuevo tramo)'
                          : 'La entrega ya está finalizada',
                        icon: <GitBranchPlus size={12} />,
                        onClick: onContinueLeg,
                        enabled: canContinue,
                        tone: 'continue',
                      })}
                      {actionBtn({
                        label: 'Finalizar',
                        title: canFinish
                          ? 'Finalizar entrega (Recepción Conforme o con Incidente)'
                          : 'La entrega ya está finalizada',
                        icon: <Check size={12} />,
                        onClick: onFinishDelivery,
                        enabled: canFinish,
                        tone: 'finish',
                      })}
                    </>
                  )}
                </div>
              );
            })()}
          </td>
        ) : null}
      </>
    );
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{title}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd && addLabel ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addLabel}
          </button>
        ) : null}
      </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={config.searchPlaceholder}
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            {config.statusFilterLabel}
          </span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            {config.typeFilterLabel}
          </span>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '2px',
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: config.minWidth }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: col.sortKey ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => col.sortKey && handleSortClick(col.sortKey)}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent:
                        col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
                      gap: 4,
                      width: col.align === 'center' || col.align === 'right' ? '100%' : 'auto',
                    }}
                  >
                    {col.key}
                    {col.sortKey ? <SortIcon columnKey={col.sortKey} /> : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  {config.loadingLabel}
                </td>
              </tr>
            ) : error && rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  {hasActiveFilters ? 'Ningún resultado con los filtros actuales.' : config.emptyLabel}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(Number(row.id));
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: selected ? rowHover : 'transparent',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : 'transparent';
                    }}
                  >
                    {showBulkChrome ? (
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar fila ${row.id}`}
                        />
                      </td>
                    ) : null}
                    {renderCells(row)}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LogisticsTable3;
